import { useTranslation } from 'react-i18next';
import {
  AlgComponentType,
  UserRequirement,
  VirtualSlot,
  ShiftAssignment,
  UserPreferenceType,
  UserPreference,
  UserPreferenceId,
  ItrStatuses,
  UserRequirementId,
  CrossGroupIncompatibilityRule,
  SingleGroupIncompatibilityRule,
} from '@youshift/shared/types';
import { useState, useEffect, useMemo } from 'react';
import { Tab } from '@headlessui/react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import {
  useSetComponentPriorityMutation,
  ComponentPrioritySchema,
} from '@youshift/shared/hooks/mutations';
import { CheckBadgeIcon } from '@heroicons/react/24/outline';

import Modal from '../Modal';
import { useItrContext } from '../../layouts/IterationRootLayout/IterationRootLayout';
import {
  AlgComponentTypeSupportingPriority,
  AlgorithmSettingsModalProps,
  IMPORTANCE_LEVEL_MAX_VALUES,
  ImportanceLevel,
  UNBREAKABLE_LEVEL_VALUE,
} from './types';
import UserReqPriority from './UserReqPriority';
import VirtualSlotPriority from './VirtualSlotPriority';
import IncompatibilitiesPriority from './IncompatibilitiesPriority';
import PreassignedShiftPriority from './PreassignedShiftPriority';
import UserBlocksPriority from './UserBlocksPriority';
import InfoIcon from '../InfoIcon';
import ImportanceInput from './ImportanceInput';
import {
  getMinComponentTypeFromUserReq,
  getMaxComponentTypeFromUserReq,
} from './utils';

const buildInitialImportanceValues = (
  userRequirements: Record<UserRequirementId, UserRequirement>,
  virtualSlots: Record<number, VirtualSlot>,
  shiftAssignments: Record<number, ShiftAssignment>,
  userPreferences: Record<number, UserPreference[]>,
  singleGroupIncompatibilities: Record<number, SingleGroupIncompatibilityRule>,
  crossGroupIncompatibilities: Record<number, CrossGroupIncompatibilityRule>,
): ComponentPrioritySchema => {
  const initialValues: ComponentPrioritySchema = {
    [AlgComponentType.VIRTUAL_SLOT_NEEDS_MIN]: {},
    [AlgComponentType.VIRTUAL_SLOT_NEEDS_MAX]: {},
    [AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_SLOTS]: {},
    [AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_SLOTS]: {},
    [AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_DURATION]: {},
    [AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_DURATION]: {},
    [AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_COUNTER_UNIT]: {},
    [AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_COUNTER_UNIT]: {},
    [AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MIN]: {},
    [AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MAX]: {},
    [AlgComponentType.CROSS_GROUP_INCOMPATIBILITY]: {},
    [AlgComponentType.C_PERSONAL_BLOCKING]: {},
    [AlgComponentType.C_JUSTIFIED_BLOCKING]: {},
    [AlgComponentType.C_SPECIAL_EVENTS]: {},
    [AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS]: {},
  };

  // Extract priorities from virtual slots
  Object.values(virtualSlots).forEach(slot => {
    if (slot.min_need_priority_coef !== null) {
      initialValues[AlgComponentType.VIRTUAL_SLOT_NEEDS_MIN][
        Number(slot.id_virtual_slot)
      ] = slot.min_need_priority_coef;
    }
    if (slot.max_need_priority_coef !== null) {
      initialValues[AlgComponentType.VIRTUAL_SLOT_NEEDS_MAX][
        Number(slot.id_virtual_slot)
      ] = slot.max_need_priority_coef;
    }
  });

  // Extract priorities from user requirements
  Object.values(userRequirements).forEach(req => {
    initialValues[getMinComponentTypeFromUserReq(req)][
      Number(req.id_user_requirement)
    ] = req.min_priority_coef;
    initialValues[getMaxComponentTypeFromUserReq(req)][
      Number(req.id_user_requirement)
    ] = req.max_priority_coef;
  });

  // Extract priorities from shift assignments
  Object.values(shiftAssignments).forEach(assignment => {
    initialValues[AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS][
      assignment.id_shift_assignment
    ] = assignment.priority_coef;
  });

  // Extract priorities from user preferences
  Object.values(userPreferences).forEach(userPrefs => {
    userPrefs.forEach(userPref => {
      const componentType =
        userPref.preference === UserPreferenceType.PERSONAL_BLOCKING
          ? AlgComponentType.C_PERSONAL_BLOCKING
          : AlgComponentType.C_JUSTIFIED_BLOCKING;

      initialValues[componentType][userPref.id_user_pref] =
        userPref.priority_coef;
    });
  });

  // Extract priorities for the incompatibilities
  Object.values(singleGroupIncompatibilities).forEach(incomp => {
    initialValues[AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MIN][
      Number(incomp.id_rule)
    ] = incomp.min_simult_priority_coef;
    initialValues[AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MAX][
      Number(incomp.id_rule)
    ] = incomp.max_simult_priority_coef;
  });

  return initialValues;
};

const calculateDiffs = (
  importanceValues: ComponentPrioritySchema,
  initialImportanceValues: ComponentPrioritySchema | null,
): Partial<ComponentPrioritySchema> => {
  if (!initialImportanceValues) return importanceValues;

  const diffs: Partial<ComponentPrioritySchema> = {};

  // Handle virtual slots
  const virtualSlotTypes: AlgComponentTypeSupportingPriority[] = [
    AlgComponentType.VIRTUAL_SLOT_NEEDS_MIN,
    AlgComponentType.VIRTUAL_SLOT_NEEDS_MAX,
  ];

  virtualSlotTypes.forEach(type => {
    const currentValues = importanceValues[type];
    const initialValues = initialImportanceValues[type];

    if (!currentValues || !initialValues) return;

    const typeDiffs: Record<number, number> = {};
    Object.entries(currentValues).forEach(([id, value]) => {
      const initialValue = initialValues[Number(id)];
      if (initialValue !== value) {
        typeDiffs[Number(id)] = value;
      }
    });

    if (Object.keys(typeDiffs).length > 0) {
      diffs[type] = typeDiffs;
    }
  });

  // Handle user requirements
  const userReqTypes: AlgComponentTypeSupportingPriority[] = [
    AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_SLOTS,
    AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_SLOTS,
    AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_DURATION,
    AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_DURATION,
    AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_COUNTER_UNIT,
    AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_COUNTER_UNIT,
  ];

  userReqTypes.forEach(type => {
    const currentValues = importanceValues[type];
    const initialValues = initialImportanceValues[type];
    if (!currentValues || !initialValues) return;
    const typeDiffs: Record<number, number> = {};
    Object.entries(currentValues).forEach(([id, value]) => {
      const initialValue = initialValues[Number(id)];
      if (initialValue !== value) {
        typeDiffs[Number(id)] = value;
      }
    });

    if (Object.keys(typeDiffs).length > 0) {
      diffs[type] = typeDiffs;
    }
  });

  // Handle incompatibilities
  const incompatibilityTypes: AlgComponentTypeSupportingPriority[] = [
    AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MIN,
    AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MAX,
    AlgComponentType.CROSS_GROUP_INCOMPATIBILITY,
  ];

  incompatibilityTypes.forEach(type => {
    const currentValues = importanceValues[type];
    const initialValues = initialImportanceValues[type];

    if (!currentValues || !initialValues) return;

    const typeDiffs: Record<number, number> = {};
    Object.entries(currentValues).forEach(([id, value]) => {
      const initialValue = initialValues[Number(id)];
      if (initialValue !== value) {
        typeDiffs[Number(id)] = value;
      }
    });

    if (Object.keys(typeDiffs).length > 0) {
      diffs[type] = typeDiffs;
    }
  });

  // Handle blocks
  const blockTypes: AlgComponentTypeSupportingPriority[] = [
    AlgComponentType.C_PERSONAL_BLOCKING,
    AlgComponentType.C_JUSTIFIED_BLOCKING,
    AlgComponentType.C_SPECIAL_EVENTS,
  ];

  blockTypes.forEach(type => {
    const currentValues = importanceValues[type];
    const initialValues = initialImportanceValues[type];

    if (!currentValues || !initialValues) return;

    const typeDiffs: Record<number, number> = {};
    Object.entries(currentValues).forEach(([id, value]) => {
      const initialValue = initialValues[Number(id)];
      if (initialValue !== value) {
        typeDiffs[Number(id)] = value;
      }
    });

    if (Object.keys(typeDiffs).length > 0) {
      diffs[type] = typeDiffs;
    }
  });

  // Handle preassigned shifts
  const currentValues =
    importanceValues[AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS];
  const initialValues =
    initialImportanceValues[AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS];

  if (currentValues && initialValues) {
    const typeDiffs: Record<number, number> = {};
    Object.entries(currentValues).forEach(([id, value]) => {
      const initialValue = initialValues[Number(id)];
      if (initialValue !== value) {
        typeDiffs[Number(id)] = value;
      }
    });

    // Also check for values that exist in initial but not in current
    Object.entries(initialValues).forEach(([id, value]) => {
      if (!(Number(id) in currentValues)) {
        typeDiffs[Number(id)] = 0; // If a value was removed, we need to set it to 0
      }
    });

    if (Object.keys(typeDiffs).length > 0) {
      diffs[AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS] = typeDiffs;
    }
  }

  return diffs;
};

export default function AlgorithmSettingsModal({
  isOpen,
  onClose,
}: AlgorithmSettingsModalProps) {
  const { t } = useTranslation();
  const {
    incompatibilities,
    allUserReqRules,
    userReqs,
    sectionsWithSlots,
    sectionSlotsDict,
    users,
    ruleAttributes,
    iteration,
    shiftAssignments,
    userPreferences,
    prefLabels,
    preferenceSlots,
  } = useItrContext();

  const [importanceValues, setImportanceValues] =
    useState<ComponentPrioritySchema>({
      [AlgComponentType.VIRTUAL_SLOT_NEEDS_MIN]: {},
      [AlgComponentType.VIRTUAL_SLOT_NEEDS_MAX]: {},
      [AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MIN]: {},
      [AlgComponentType.SINGLE_GROUP_INCOMPATIBILITY_MAX]: {},
      [AlgComponentType.CROSS_GROUP_INCOMPATIBILITY]: {},
      [AlgComponentType.C_PERSONAL_BLOCKING]: {},
      [AlgComponentType.C_JUSTIFIED_BLOCKING]: {},
      [AlgComponentType.C_SPECIAL_EVENTS]: {},
      [AlgComponentType.C_PREVIOUSLY_ASSIGNED_SHIFTS]: {},
      [AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_SLOTS]: {},
      [AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_SLOTS]: {},
      [AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_DURATION]: {},
      [AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_DURATION]: {},
      [AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_COUNTER_UNIT]: {},
      [AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_COUNTER_UNIT]: {},
    });

  const [initialImportanceValues, setInitialImportanceValues] =
    useState<ComponentPrioritySchema>(
      buildInitialImportanceValues(
        userReqs,
        sectionsWithSlots.reduce(
          (acc, section) => ({ ...acc, ...section.virtual_slots }),
          {} as Record<number, VirtualSlot>,
        ),
        shiftAssignments,
        userPreferences,
        incompatibilities.single_group_incomp,
        incompatibilities.cross_group_incomp,
      ),
    );

  useEffect(() => {
    // Initialize importance values from the database
    const initialImportanceValues = buildInitialImportanceValues(
      userReqs,
      sectionsWithSlots.reduce(
        (acc, section) => ({ ...acc, ...section.virtual_slots }),
        {} as Record<number, VirtualSlot>,
      ),
      shiftAssignments,
      userPreferences,
      incompatibilities.single_group_incomp,
      incompatibilities.cross_group_incomp,
    );

    setInitialImportanceValues(initialImportanceValues);
    setImportanceValues(prev => ({
      ...prev,
      ...initialImportanceValues,
    }));
  }, [
    userReqs,
    sectionsWithSlots,
    shiftAssignments,
    userPreferences,
    incompatibilities,
  ]);

  const hasChanges = useMemo(() => {
    if (!initialImportanceValues) return false;

    return Object.entries(importanceValues).some(([type, values]) => {
      const initialValues =
        initialImportanceValues[type as AlgComponentTypeSupportingPriority];
      if (!initialValues) return true;

      return Object.entries(values as Record<number, number>).some(
        ([id, value]) => {
          return initialValues[Number(id)] !== value;
        },
      );
    });
  }, [importanceValues, initialImportanceValues]);

  const queryClient = useQueryClient();

  const setComponentPriorityMutation = useSetComponentPriorityMutation(
    queryClient,
    {
      onSuccess: () => {
        setInitialImportanceValues(importanceValues);
        onClose();
        toast.success(t('generic.success'));
      },
    },
  );

  const handleSave = () => {
    if (!iteration?.id_itr) return;

    const diffs = calculateDiffs(importanceValues, initialImportanceValues);
    setComponentPriorityMutation.mutate({
      id_itr: iteration.id_itr,
      id_chain: iteration.id_chain,
      components_priority: diffs,
    });
  };

  // Filter userPreferences to only include block types
  // And index by UserPreferenceId
  const blockingUserPreferences = useMemo(() => {
    const result: Record<UserPreferenceId, UserPreference> = {};

    Object.entries(userPreferences).forEach(([idPrefSlot, prefs]) => {
      const blocks = prefs.filter(
        pref =>
          pref.preference === UserPreferenceType.PERSONAL_BLOCKING ||
          pref.preference === UserPreferenceType.JUSTIFIED_BLOCKING,
      );
      for (const block of blocks) {
        result[block.id_user_pref] = block;
      }
    });

    return result;
  }, [userPreferences]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <div className="w-full">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">
                {t(
                  'manager.smartass.algorithmSettingsModal.algorithmSettingsModalTitle',
                )}
              </h2>
              <InfoIcon
                title={t(
                  'manager.smartass.algorithmSettingsModal.algorithmSettingsModalTitle',
                )}
                content={
                  <>
                    <p className="pt-4">
                      {t(
                        'manager.smartass.algorithmSettingsModal.algorithmSettingsModalSubtitle',
                      )}
                    </p>
                    <p className="mt-2">
                      {t(
                        'manager.smartass.algorithmSettingsModal.algorithmSettingsModalSubtitle2',
                      )}
                    </p>
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-5 w-5 text-yellow-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">
                            {t(
                              'manager.smartass.algorithmSettingsModal.excessUnbreakablePriority.title',
                            )}
                          </h3>
                          <div className="mt-2 text-sm text-yellow-700">
                            <p className="text-justify">
                              {t(
                                'manager.smartass.algorithmSettingsModal.excessUnbreakablePriority.warningMessage',
                              )}
                            </p>
                            <div className="flex items-start gap-2 mt-2">
                              <p className="text-justify">
                                {t(
                                  'manager.smartass.algorithmSettingsModal.excessUnbreakablePriority.warningMessage2',
                                )}
                              </p>
                              <div className="flex flex-col gap-2 p-2 bg-gray-50 border border-gray-200 rounded-lg relative">
                                <div className="absolute -top-2 -right-2 z-10">
                                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-800">
                                    <CheckBadgeIcon className="h-5 w-5" />
                                  </span>
                                </div>
                                <ImportanceInput
                                  value={UNBREAKABLE_LEVEL_VALUE}
                                  onChange={() => {}}
                                  className="opacity-90 pointer-events-none [&_input]:cursor-not-allowed [&_button]:cursor-not-allowed [&_input]:bg-white [&_button]:bg-white"
                                />
                                <div className="flex items-center justify-center -mt-1 -mb-1">
                                  <svg
                                    className="h-4 w-4 text-gray-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                                    />
                                  </svg>
                                </div>
                                <ImportanceInput
                                  value={
                                    IMPORTANCE_LEVEL_MAX_VALUES[
                                      ImportanceLevel.VERY_HIGH
                                    ]
                                  }
                                  onChange={() => {}}
                                  className="opacity-90 pointer-events-none [&_input]:cursor-not-allowed [&_button]:cursor-not-allowed [&_input]:bg-white [&_button]:bg-white"
                                />
                              </div>
                            </div>
                            <p className="mt-2 text-justify">
                              {t(
                                'manager.smartass.algorithmSettingsModal.excessUnbreakablePriority.warningMessage3',
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                }
                className="w-4 h-4"
                iconSize={6}
                size="2xl"
              />
            </div>
          </div>
          {hasChanges && (
            <button
              onClick={handleSave}
              disabled={iteration?.status === ItrStatuses.RUNNING_SMARTASS}
              className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                iteration?.status === ItrStatuses.RUNNING_SMARTASS
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {t('generic.save')}
            </button>
          )}
        </div>
        <Tab.Group>
          <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-4">
            <Tab
              className={({ selected }) =>
                `w-full rounded-lg py-2.5 text-sm font-medium leading-5
                ${
                  selected
                    ? 'bg-white text-blue-700 shadow'
                    : 'text-gray-700 hover:bg-white/[0.12] hover:text-blue-600'
                }`
              }
            >
              <div className="flex items-center justify-center">
                {t('generic.virtualSlots')}
              </div>
            </Tab>
            <Tab
              className={({ selected }) =>
                `w-full rounded-lg py-2.5 text-sm font-medium leading-5
                ${
                  selected
                    ? 'bg-white text-blue-700 shadow'
                    : 'text-gray-700 hover:bg-white/[0.12] hover:text-blue-600'
                }`
              }
            >
              <div className="flex items-center justify-center">
                {t('generic.rules')}
              </div>
            </Tab>
            {(Object.keys(incompatibilities.single_group_incomp).length > 0 ||
              Object.keys(incompatibilities.cross_group_incomp).length > 0) && (
              <Tab
                className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5
                  ${
                    selected
                      ? 'bg-white text-blue-700 shadow'
                      : 'text-gray-700 hover:bg-white/[0.12] hover:text-blue-600'
                  }`
                }
              >
                <div className="flex items-center justify-center">
                  {t('generic.incompatibilities')}
                </div>
              </Tab>
            )}
            {Object.keys(shiftAssignments).length > 0 && (
              <Tab
                className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5
                  ${
                    selected
                      ? 'bg-white text-blue-700 shadow'
                      : 'text-gray-700 hover:bg-white/[0.12] hover:text-blue-600'
                  }`
                }
              >
                <div className="flex items-center justify-center">
                  {t('generic.preassignedShifts')}
                </div>
              </Tab>
            )}
            {Object.keys(blockingUserPreferences).length > 0 && (
              <Tab
                className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5
                ${
                  selected
                    ? 'bg-white text-blue-700 shadow'
                    : 'text-gray-700 hover:bg-white/[0.12] hover:text-blue-600'
                }`
                }
              >
                <div className="flex items-center justify-center">
                  {t('generic.blocks')}
                </div>
              </Tab>
            )}
          </Tab.List>
          <Tab.Panels>
            <Tab.Panel>
              <VirtualSlotPriority
                sectionsWithSlots={sectionsWithSlots}
                importanceValues={importanceValues}
                setImportanceValues={setImportanceValues}
              />
            </Tab.Panel>
            <Tab.Panel>
              <UserReqPriority
                allUserReqRules={allUserReqRules}
                ruleAttributes={ruleAttributes}
                users={users}
                importanceValues={importanceValues}
                setImportanceValues={setImportanceValues}
              />
            </Tab.Panel>
            {(Object.keys(incompatibilities.single_group_incomp).length > 0 ||
              Object.keys(incompatibilities.cross_group_incomp).length > 0) && (
              <Tab.Panel>
                <IncompatibilitiesPriority
                  incompatibilities={incompatibilities}
                  importanceValues={importanceValues}
                  setImportanceValues={setImportanceValues}
                />
              </Tab.Panel>
            )}
            {Object.keys(shiftAssignments).length > 0 && (
              <Tab.Panel>
                <PreassignedShiftPriority
                  users={users}
                  shiftAssignments={shiftAssignments}
                  importanceValues={importanceValues}
                  setImportanceValues={setImportanceValues}
                />
              </Tab.Panel>
            )}
            {Object.keys(blockingUserPreferences).length > 0 && (
              <Tab.Panel>
                <UserBlocksPriority
                  users={users}
                  importanceValues={importanceValues}
                  setImportanceValues={setImportanceValues}
                  userPreferences={blockingUserPreferences}
                  prefLabels={prefLabels}
                  preferenceSlots={preferenceSlots}
                  sectionSlotsDict={sectionSlotsDict}
                />
              </Tab.Panel>
            )}
          </Tab.Panels>
        </Tab.Group>
      </div>
    </Modal>
  );
}
